import { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Download, Gift, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { PaymentLinkPayment, PaymentLinkPaymentStatus } from '@masonic-tools/shared-types';
import {
  DEFAULT_PAYMENT_FILTERS,
  filterPayments,
  formatDateTime,
  formatPence,
  METHOD_PROVIDER_NAMES,
  sortPayments,
  totalPayments,
  type PaymentFilters,
  type SortKey,
} from '@/lib/unit-payment-links';

const PAGE_SIZE = 25;

export const PAYMENT_STATUS_VARIANTS: Record<PaymentLinkPaymentStatus, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  paid: 'success',
  pending: 'warning',
  failed: 'destructive',
  cancelled: 'secondary',
};

export const PAYMENT_STATUS_LABELS: Record<PaymentLinkPaymentStatus, string> = {
  paid: 'Paid',
  pending: 'Pending',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

interface Props {
  payments: PaymentLinkPayment[];
  loading: boolean;
  highlightIds?: Set<string>;
  onSelect: (payment: PaymentLinkPayment) => void;
  onExport: (filtered: PaymentLinkPayment[]) => void;
}

const COLUMNS: Array<{ key: SortKey; label: string; align?: 'right' }> = [
  { key: 'date', label: 'Date' },
  { key: 'payer', label: 'Payer' },
  { key: 'amount', label: 'Amount', align: 'right' },
  { key: 'fee', label: 'Fee', align: 'right' },
  { key: 'total', label: 'Charged', align: 'right' },
  { key: 'net', label: 'Net', align: 'right' },
  { key: 'method', label: 'Method' },
  { key: 'giftAid', label: 'Gift Aid' },
  { key: 'status', label: 'Status' },
];

export function PaymentsTable({ payments, loading, highlightIds, onSelect, onExport }: Props) {
  const [filters, setFilters] = useState<PaymentFilters>(DEFAULT_PAYMENT_FILTERS);
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'date', dir: 'desc' });
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => filterPayments(payments, filters), [payments, filters]);
  const sorted = useMemo(() => sortPayments(filtered, sort.key, sort.dir), [filtered, sort]);
  const totals = useMemo(() => totalPayments(filtered), [filtered]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const current = Math.min(page, pageCount - 1);
  const rows = sorted.slice(current * PAGE_SIZE, current * PAGE_SIZE + PAGE_SIZE);

  const update = (patch: Partial<PaymentFilters>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(0);
  };
  const toggleSort = (key: SortKey) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'payer' || key === 'method' ? 'asc' : 'desc' }));

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col gap-2 lg:flex-row lg:items-end">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search name, email or transaction ID"
            aria-label="Search payments"
            value={filters.search}
            onChange={(e) => update({ search: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:flex">
          <Select value={filters.status} onValueChange={(v) => update({ status: v as PaymentFilters['status'] })}>
            <SelectTrigger className="lg:w-36" aria-label="Status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All statuses</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.giftAid} onValueChange={(v) => update({ giftAid: v as PaymentFilters['giftAid'] })}>
            <SelectTrigger className="lg:w-40" aria-label="Gift Aid">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All payments</SelectItem>
              <SelectItem value="yes">Gift Aid only</SelectItem>
              <SelectItem value="no">No Gift Aid</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filters.dateRange} onValueChange={(v) => update({ dateRange: v as PaymentFilters['dateRange'] })}>
            <SelectTrigger className="lg:w-36" aria-label="Date range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="month">This month</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => onExport(filtered)} disabled={payments.length === 0}>
            <Download className="mr-2 h-4 w-4" /> Export
          </Button>
        </div>
      </div>
      {filters.dateRange === 'custom' ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="flex items-center gap-2">
            From <Input type="date" className="w-auto" value={filters.customFrom || ''} onChange={(e) => update({ customFrom: e.target.value })} />
          </label>
          <label className="flex items-center gap-2">
            To <Input type="date" className="w-auto" value={filters.customTo || ''} onChange={(e) => update({ customTo: e.target.value })} />
          </label>
        </div>
      ) : null}

      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Loading payments…</p>
      ) : payments.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-10 text-center">
          <p className="font-medium">No payments yet</p>
          <p className="text-sm text-muted-foreground">Payments appear here as soon as they are made.</p>
        </div>
      ) : sorted.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-10 text-center">
          <p className="font-medium">No payments match these filters</p>
          <Button variant="link" onClick={() => update(DEFAULT_PAYMENT_FILTERS)}>
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left">
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} scope="col" className={cn('px-3 py-2 font-medium', c.align === 'right' && 'text-right')} aria-sort={sort.key === c.key ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                      <button type="button" onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-foreground">
                        {c.label}
                        {sort.key === c.key ? sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" /> : null}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={p.id}
                    tabIndex={0}
                    onClick={() => onSelect(p)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onSelect(p))}
                    className={cn(
                      'cursor-pointer border-t border-border transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none',
                      highlightIds?.has(p.id) && 'animate-pulse bg-green-500/10',
                    )}
                  >
                    <td className="whitespace-nowrap px-3 py-2">{formatDateTime(p.paidAt || p.createdAt)}</td>
                    <td className="max-w-[220px] px-3 py-2">
                      {p.anonymous ? (
                        <Badge variant="secondary">Anonymous</Badge>
                      ) : (
                        <>
                          <div className="truncate font-medium">{p.payerName}</div>
                          <div className="truncate text-xs text-muted-foreground">{p.payerEmail}</div>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatPence(p.amountPence)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{formatPence(p.processingFeePence)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatPence(p.totalChargedPence)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatPence(p.netToUnitPence)}</td>
                    <td className="px-3 py-2">{METHOD_PROVIDER_NAMES[p.method]}</td>
                    <td className="px-3 py-2">{p.giftAid ? <Gift className="h-4 w-4 text-primary" aria-label="Gift Aid" /> : null}</td>
                    <td className="px-3 py-2">
                      <Badge variant={PAYMENT_STATUS_VARIANTS[p.status]}>{PAYMENT_STATUS_LABELS[p.status]}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t-2 border-border bg-muted/30 font-medium">
                <tr>
                  <td className="px-3 py-2" colSpan={2}>
                    {totals.count} paid payment{totals.count === 1 ? '' : 's'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatPence(totals.grossPence)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatPence(totals.feesPence)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatPence(totals.totalChargedPence)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatPence(totals.netPence)}</td>
                  <td className="px-3 py-2" colSpan={3}>
                    Gift Aid: {formatPence(totals.giftAidEligiblePence)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Mobile cards */}
          <ul className="space-y-2 md:hidden">
            {rows.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  onClick={() => onSelect(p)}
                  className={cn(
                    'w-full rounded-lg border border-border p-3 text-left transition-colors hover:bg-muted/50',
                    highlightIds?.has(p.id) && 'animate-pulse bg-green-500/10',
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-lg font-semibold tabular-nums">{formatPence(p.amountPence)}</span>
                    <span className="flex items-center gap-2">
                      {p.giftAid ? <Gift className="h-4 w-4 text-primary" aria-label="Gift Aid" /> : null}
                      <Badge variant={PAYMENT_STATUS_VARIANTS[p.status]}>{PAYMENT_STATUS_LABELS[p.status]}</Badge>
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2 text-sm text-muted-foreground">
                    <span className="truncate">{p.anonymous ? 'Anonymous' : p.payerName}</span>
                    <span className="shrink-0">{formatDateTime(p.paidAt || p.createdAt)}</span>
                  </div>
                </button>
              </li>
            ))}
            <li className="rounded-lg bg-muted/50 p-3 text-sm">
              <strong>{totals.count}</strong> paid · {formatPence(totals.grossPence)} gross · {formatPence(totals.netPence)} net
            </li>
          </ul>

          {pageCount > 1 ? (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Page {current + 1} of {pageCount} · {sorted.length} payments
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={current === 0} onClick={() => setPage(current - 1)}>
                  Previous
                </Button>
                <Button size="sm" variant="outline" disabled={current >= pageCount - 1} onClick={() => setPage(current + 1)}>
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
